import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Pause, 
  Square, 
  Download, 
  Trash2, 
  Search,
  Eye,
  Terminal,
  Activity,
  Clock,
  Zap,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import Header from '../components/layout/Header'
import { useWorkflowStore } from '../stores/workflowStore'

const KickoffPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'logs' | 'outputs' | 'results' | 'debug'>('logs')
  const [autoScroll, setAutoScroll] = useState(true)
  const [logFilter, setLogFilter] = useState<'all' | 'info' | 'warning' | 'error' | 'debug'>('all')
  
  const { 
    currentExecution,
    executionLogs,
    isExecuting,
    pauseExecution,
    stopExecution,
    connectToExecution,
    disconnectFromExecution
  } = useWorkflowStore()

  useEffect(() => {
    if (workflowId) {
      connectToExecution(workflowId)
    }
    
    return () => {
      disconnectFromExecution()
    }
  }, [workflowId, connectToExecution, disconnectFromExecution])

  const handlePause = async () => {
    if (workflowId) {
      await pauseExecution(workflowId)
    }
  }

  const handleStop = async () => {
    if (workflowId && confirm('Are you sure you want to stop the workflow execution?')) {
      await stopExecution(workflowId)
      navigate('/dashboard')
    }
  }

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getLogColor = (level: string) => {
    switch (level) {
      case 'info': return 'text-green-400'
      case 'warning': return 'text-yellow-400'
      case 'error': return 'text-red-400'
      case 'debug': return 'text-blue-400'
      default: return 'text-white'
    }
  }

  const filteredLogs = executionLogs.filter(log => 
    logFilter === 'all' || log.level === logFilter
  )

  const getExecutionTime = () => {
    if (!currentExecution) return '00:00:00'
    
    const start = new Date(currentExecution.startTime)
    const end = currentExecution.endTime ? new Date(currentExecution.endTime) : new Date()
    const diff = Math.floor((end.getTime() - start.getTime()) / 1000)
    
    const hours = Math.floor(diff / 3600).toString().padStart(2, '0')
    const minutes = Math.floor((diff % 3600) / 60).toString().padStart(2, '0')
    const seconds = (diff % 60).toString().padStart(2, '0')
    
    return `${hours}:${minutes}:${seconds}`
  }

  const nodeStatuses = [
    { 
      id: 'research-agent',
      name: 'Research Agent', 
      icon: '🔍',
      status: executionLogs.length > 5 ? 'completed' : executionLogs.length > 2 ? 'running' : 'waiting',
      duration: executionLogs.length > 5 ? '45s' : executionLogs.length > 2 ? `${Math.floor(Math.random() * 30) + 10}s` : null,
      output: executionLogs.length > 5 ? 'Analyzed 247 market data points' : executionLogs.length > 2 ? 'Processing data sources...' : null
    },
    { 
      id: 'writer-agent',
      name: 'Writer Agent', 
      icon: '✍️',
      status: executionLogs.length > 10 ? 'completed' : executionLogs.length > 7 ? 'running' : 'waiting',
      duration: executionLogs.length > 10 ? '1m 34s' : executionLogs.length > 7 ? `${Math.floor(Math.random() * 60) + 30}s` : null,
      output: executionLogs.length > 10 ? 'Generated 1,247 words of content' : executionLogs.length > 7 ? 'Writing executive summary...' : null
    },
    { 
      id: 'analysis-task',
      name: 'Analysis Task', 
      icon: '📊',
      status: executionLogs.length > 13 ? 'completed' : executionLogs.length > 11 ? 'running' : 'waiting',
      duration: executionLogs.length > 13 ? '28s' : executionLogs.length > 11 ? `${Math.floor(Math.random() * 20) + 5}s` : null,
      output: executionLogs.length > 13 ? 'Final performance analysis complete' : executionLogs.length > 11 ? 'Analyzing results...' : null
    }
  ]

  return (
    <div className="h-screen flex flex-col bg-background">
      <Header
        workflowName="Marketing Campaign Crew"
        showWorkflowControls={true}
        isExecuting={isExecuting}
        onPause={handlePause}
        onStop={handleStop}
      />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Workflow Overview */}
        <aside className="w-80 sidebar border-r overflow-y-auto">
          {/* Execution Progress */}
          <div className="p-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Execution Progress
              </h2>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {nodeStatuses.filter(n => n.status === 'completed').length}/{nodeStatuses.length} Complete
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-900 dark:bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${currentExecution?.progress || 0}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div className="flex justify-between mb-1">
                <span>Elapsed Time</span>
                <span className="font-mono">{getExecutionTime()}</span>
              </div>
              <div className="flex justify-between">
                <span>Est. Remaining</span>
                <span className="font-mono">
                  {isExecuting ? '01:45' : '00:00'}
                </span>
              </div>
            </div>
          </div>

          {/* Node Status List */}
          <div className="p-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Node Status</h3>
            <div className="space-y-3">
              {nodeStatuses.map((node) => (
                <div 
                  key={node.id}
                  className={`card p-4 ${
                    node.status === 'running' 
                      ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20' 
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{node.icon}</span>
                      <span className="font-medium text-sm text-gray-900 dark:text-white">
                        {node.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {node.status === 'completed' && (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                            COMPLETED
                          </span>
                        </>
                      )}
                      {node.status === 'running' && (
                        <>
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            RUNNING
                          </span>
                        </>
                      )}
                      {node.status === 'waiting' && (
                        <>
                          <Clock className="w-4 h-4 text-yellow-500" />
                          <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                            WAITING
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {node.duration && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Duration: {node.duration}
                    </div>
                  )}
                  {node.output && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                      {node.output}
                    </div>
                  )}
                  {node.status === 'running' && (
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                      <div className="bg-blue-500 h-1 rounded-full transition-all duration-500" style={{ width: '75%' }}></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Mission Control Panel */}
        <main className="flex-1 flex flex-col">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <nav className="flex space-x-8">
              {[
                { id: 'logs', label: 'Live Logs', icon: Terminal },
                { id: 'outputs', label: 'Intermediate Outputs', icon: Eye },
                { id: 'results', label: 'Final Results', icon: CheckCircle },
                { id: 'debug', label: 'Debug Info', icon: Activity }
              ].map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-blue-900 dark:border-blue-400 text-blue-900 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="flex-1 flex flex-col">
            {activeTab === 'logs' && (
              <>
                {/* Log Controls */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          checked={autoScroll}
                          onChange={(e) => setAutoScroll(e.target.checked)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Auto-scroll</span>
                      </label>
                      <select 
                        value={logFilter}
                        onChange={(e) => setLogFilter(e.target.value as any)}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="all">All Levels</option>
                        <option value="info">Info</option>
                        <option value="warning">Warning</option>
                        <option value="error">Error</option>
                        <option value="debug">Debug</option>
                      </select>
                      <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center space-x-1">
                        <Search className="w-4 h-4" />
                        <span>Search logs</span>
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center space-x-1">
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                      </button>
                      <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center space-x-1">
                        <Trash2 className="w-4 h-4" />
                        <span>Clear</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Terminal Output */}
                <div className="flex-1 terminal terminal-dark p-4 overflow-y-auto text-sm">
                  <div className="space-y-1">
                    {filteredLogs.map((log) => (
                      <div key={log.id} className={`log-entry ${getLogColor(log.level)}`}>
                        [{formatTimestamp(log.timestamp)}] {log.level.toUpperCase()}: {log.message}
                      </div>
                    ))}
                    {isExecuting && (
                      <div className="text-blue-400 animate-pulse">
                        [{formatTimestamp(new Date())}] INFO: Execution in progress... ▊
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'outputs' && (
              <div className="flex-1 p-6">
                <div className="space-y-4">
                  <div className="card p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Research Agent Output</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Market analysis data collected from 15 sources including industry reports, competitor analysis, and market trends...
                    </div>
                  </div>
                  {executionLogs.length > 7 && (
                    <div className="card p-4">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2">Writer Agent Output</h3>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        <strong>Executive Summary:</strong> The AI automation market shows unprecedented growth potential with a projected 47% year-over-year increase...
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'results' && (
              <div className="flex-1 p-6">
                {currentExecution?.status === 'completed' ? (
                  <div className="card p-6">
                    <div className="flex items-center mb-4">
                      <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Workflow Completed Successfully
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Final Report</h4>
                        <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          Complete market analysis report with executive summary, detailed findings, and strategic recommendations has been generated successfully.
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="btn btn-primary">
                          <Download className="w-4 h-4 mr-2" />
                          Download Report
                        </button>
                        <button className="btn btn-secondary">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Results Not Available</h3>
                    <p>Final results will appear here once the workflow execution is complete.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'debug' && (
              <div className="flex-1 p-6">
                <div className="space-y-4">
                  <div className="card p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">System Information</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <div>CrewAI Version: 0.22.5</div>
                      <div>Python Version: 3.9.7</div>
                      <div>Memory Usage: 847 MB</div>
                      <div>CPU Usage: 45%</div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">API Calls</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <div>Total API Calls: 1,247</div>
                      <div>Tokens Used: 45.2K</div>
                      <div>Average Response Time: 1.2s</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Right Panel - Quick Actions & Metrics */}
        <aside className="w-80 sidebar border-l overflow-y-auto">
          {/* Real-time Metrics */}
          <div className="p-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Real-time Metrics</h3>
            <div className="space-y-4">
              {[
                { label: 'CPU Usage', value: 45, color: 'blue' },
                { label: 'Memory', value: 62, color: 'green' },
              ].map((metric, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">{metric.label}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className={`bg-${metric.color}-500 h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${metric.value}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-mono text-gray-900 dark:text-white">{metric.value}%</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">API Calls</span>
                <span className="text-sm font-mono text-gray-900 dark:text-white">1,247</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">Tokens Used</span>
                <span className="text-sm font-mono text-gray-900 dark:text-white">45.2K</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-2">
              {[
                { icon: Pause, label: 'Pause Execution', action: handlePause },
                { icon: Square, label: 'Stop Execution', action: handleStop },
                { icon: Download, label: 'Export Logs', action: () => {} },
                { icon: Zap, label: 'Save Checkpoint', action: () => {} },
              ].map((action, index) => {
                const Icon = action.icon
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                    <span>{action.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Active Connections */}
          <div className="p-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">Active Connections</h3>
            <div className="space-y-3">
              {[
                { name: 'OpenAI API', status: 'connected' },
                { name: 'Web Search', status: 'active' },
                { name: 'Document Store', status: 'pending' },
              ].map((connection, index) => (
                <div key={index} className="flex items-center justify-between card p-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      connection.status === 'connected' ? 'bg-green-500' :
                      connection.status === 'active' ? 'bg-green-500' :
                      'bg-yellow-500'
                    }`}></div>
                    <span className="text-sm text-gray-900 dark:text-white">{connection.name}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {connection.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default KickoffPage
